import { useState } from "react";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import issuer_services from "services/issuer_services";
import { networkTypeSelector } from "store/userSliceHelpers";
import { apiErrorMessage } from "utils/errors/parseApiError";

export interface UseBlockTerminalReturn {
  blockingTerminal: boolean;
  blockingTerminalMsg: string;
  blockTerminalError: boolean;
  blockTerminalErrorMsg: string;
  blockTerminalSuccess: boolean;
  blockTerminalSuccessMsg: string;
  handleBlockTerminal: (serialId: string) => Promise<void>;
  handleRetry: () => Promise<void>;
  handleCloseError: () => void;
  handleCloseSuccess: () => void;
}

/**
 * Block a terminal. The contract allows it only from ACTIVE or IDLE, and BLOCKED is terminal —
 * there is no unblockPOS, so a blocked terminal never comes back.
 */
export function useBlockTerminal(): UseBlockTerminalReturn {
  const networkType = useSelector(networkTypeSelector);
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const [lastSerialId, setLastSerialId] = useState("");

  const openError = (msg: string) => {
    setLoadingMsg("");
    setIsLoading(false);
    setErrorMsg(msg);
    setShowError(true);
  };

  const handleCloseError = () => {
    setShowError(false);
    setErrorMsg("");
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setSuccessMsg("");
  };

  const handleBlockTerminal = async (serialId: string) => {
    setLastSerialId(serialId);
    // ErrorModal's "Try again" does not dismiss itself, so a retry would stack the next modal
    // on top of the open error.
    handleCloseError();

    try {
      setLoadingMsg("Blocking terminal…");
      setIsLoading(true);

      await issuer_services.blockTerminal(serialId, networkType);

      // The row reads `status` from the list query, and the KPI counts by status.
      queryClient.invalidateQueries({ queryKey: ["terminals"] });
      queryClient.invalidateQueries({ queryKey: ["terminalsCount"] });
      // Marked stale but NOT refetched: useTerminalLookup reports isFetching as "loading", which
      // swaps the searched row for a skeleton and unmounts the success modal below.
      queryClient.invalidateQueries({
        queryKey: ["terminal-lookup"],
        refetchType: "none",
      });

      setIsLoading(false);
      setLoadingMsg("");
      setSuccessMsg(`Terminal ${serialId} has been blocked.`);
      setShowSuccess(true);
    } catch (error: any) {
      openError(
        apiErrorMessage(error, "Failed to block terminal. Please try again."),
      );
    }
  };

  const handleRetry = () =>
    lastSerialId ? handleBlockTerminal(lastSerialId) : Promise.resolve();

  return {
    blockingTerminal: isLoading,
    blockingTerminalMsg: loadingMsg,
    blockTerminalError: showError,
    blockTerminalErrorMsg: errorMsg,
    blockTerminalSuccess: showSuccess,
    blockTerminalSuccessMsg: successMsg,
    handleBlockTerminal,
    handleRetry,
    handleCloseError,
    handleCloseSuccess,
  };
}
